import { useEffect, useRef, useState } from "react";
import type { ClosedPosition, OhlcCandle, PositionPriceHistoryResponse } from "@auto-pools/core";
import { api } from "../api/client";

/** Nearest candle at or before `atSec`, by binary search over the ascending cached series. */
function priceAt(candles: OhlcCandle[], atSec: number): string | null {
  let lo = 0,
    hi = candles.length - 1,
    result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((candles[mid]?.time ?? Infinity) <= atSec) {
      result = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return result === -1 ? null : (candles[result]?.close ?? null);
}

function formatElapsed(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours)}h ${String(minutes)}m`;
}

/**
 * Button + slider for a closed position's Range bar. Fetches a per-minute price series on demand,
 * caches it server-side, and reports a scrubbed price back to the parent while dragging — the
 * parent threads that price into the Range bar's marker. Releasing snaps back to `null` (the card's
 * normal closing marker), a pure UI reset with no re-fetch.
 */
export function PositionPriceScrubber({
  item,
  onScrubPriceChange,
}: {
  item: ClosedPosition;
  onScrubPriceChange: (price: string | null) => void;
}) {
  const range = item.executedRange;
  const openedAtMs = item.openedVerifiedAt ? Date.parse(item.openedVerifiedAt) : null;
  const closedAtMsRaw = item.closedVerifiedAt ? Date.parse(item.closedVerifiedAt) : NaN;
  const available = Boolean(range) && Number.isFinite(closedAtMsRaw);

  const [series, setSeries] = useState<PositionPriceHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scrubAt, setScrubAt] = useState<number | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!available) return;
    const controller = new AbortController();
    api<PositionPriceHistoryResponse>(`/positions/${item.positionId}/price-history`, {
      signal: controller.signal,
    }).then(
      (next) => {
        if (!controller.signal.aborted && next.candles.length > 0) setSeries(next);
      },
      // Nothing cached yet (or the check failed) is the ordinary "show the button" state — no
      // error surfaced for a background check the user never asked for.
      () => undefined,
    );
    return () => {
      controller.abort();
    };
  }, [item.positionId, available]);

  useEffect(() => {
    if (scrubAt === null || !series) {
      onScrubPriceChange(null);
      return;
    }
    onScrubPriceChange(priceAt(series.candles, Math.floor(scrubAt / 1000)));
  }, [scrubAt, series, onScrubPriceChange]);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  if (!available) return null;
  const closedAtMs = closedAtMsRaw;

  const load = () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    api<PositionPriceHistoryResponse>(`/positions/${item.positionId}/price-history`, {
      method: "POST",
      signal: controller.signal,
    }).then(
      (next) => {
        if (controller.signal.aborted) return;
        setSeries(next);
        setLoading(false);
      },
      (reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : "Price history could not be fetched.");
        setLoading(false);
      },
    );
  };

  if (!series || series.candles.length === 0) {
    // A response with zero candles is a real outcome (provider budget exhausted, or the pool
    // simply has no minute history), not "never fetched" — say so instead of just resetting to
    // the same "Load history" button, which would read as nothing having happened.
    const emptyReason =
      series && !error
        ? series.budgetExhausted
          ? "Price history provider budget exhausted for now — try again later."
          : series.coverage.complete
            ? "No price history is available for this position."
            : null
        : null;
    return (
      <div className="position-price-scrubber">
        <button type="button" className="button ghost small" onClick={load} disabled={loading}>
          {loading ? "Loading price history…" : error || emptyReason ? "Retry" : "Load history"}
        </button>
        {error && <small className="scrubber-error">{error}</small>}
        {!error && emptyReason && <small className="scrubber-error">{emptyReason}</small>}
      </div>
    );
  }

  const lowerBound = openedAtMs ?? series.coverage.oldestCoveredAt ?? closedAtMs;
  const value = scrubAt ?? closedAtMs;
  const release = () => {
    setScrubAt(null);
  };

  return (
    <div className="position-price-scrubber">
      <input
        type="range"
        className="scrubber-slider"
        min={lowerBound}
        max={closedAtMs}
        step={60_000}
        value={value}
        aria-label="Scrub through this position's price history"
        onChange={(event) => {
          setScrubAt(Number(event.target.value));
        }}
        onPointerUp={release}
        onKeyUp={release}
        onBlur={release}
      />
      <small className="scrubber-caption">
        {formatElapsed(value - lowerBound)} of {formatElapsed(closedAtMs - lowerBound)}
      </small>
      {!series.coverage.complete && (
        <button type="button" className="button ghost small" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Load more history"}
        </button>
      )}
      {error && <small className="scrubber-error">{error}</small>}
    </div>
  );
}
